import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn } from 'components/ui';
import type { ViewModeOption } from 'components/modals/common/ModuleHeader';
import {
  EnterpriseScreenShell, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { usePeriodLocks } from 'hooks/api/useAccounts';
import { periodLockService } from 'api/services';
import type { PeriodLockDto, PeriodLockQueryParams } from 'api/types';
import { PeriodLockFormModal } from '../../../modals/modules/accounts/PeriodLockFormModal';

type ViewMode = 'list' | 'grid';

const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString() : '—');
const SOURCE_LABEL: Record<string, string> = {
  PERIOD_CLOSE: 'Period Close', AUDIT: 'Audited', DAY_END: 'Day-End', MANUAL: 'Manual',
};

interface PeriodLockScreenProps {
  moduleIcon?: string;
  tabId?: string;
}

const PeriodLockScreen: React.FC<PeriodLockScreenProps> = ({ moduleIcon, tabId }) => {
  const can = useCan('period-lock');

  const [queryParams, setQueryParams] = useState<PeriodLockQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = usePeriodLocks(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const [filterOpen, setFilterOpen] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [unlockTarget, setUnlockTarget] = useState<PeriodLockDto | null>(null);
  const [isUnlocking, setIsUnlocking] = useState(false);

  const handlePageChange = useCallback((idx: number) => setQueryParams(p => ({ ...p, page: idx + 1 })), []);
  const handlePageSizeChange = useCallback((size: number) => setQueryParams(p => ({ ...p, pageSize: size, page: 1 })), []);

  const handleSaved = useCallback(() => { setFormOpen(false); refetch(); }, [refetch]);

  const handleConfirmUnlock = useCallback(async () => {
    if (!unlockTarget) return;
    setIsUnlocking(true);
    try {
      await periodLockService.unlock(unlockTarget.id, unlockTarget.rowVersion);
      setIsUnlocking(false);
      setUnlockTarget(null);
      notificationService.toast.success('Period unlocked.');
      refetch();
    } catch (err: any) {
      setIsUnlocking(false);
      notificationService.toast.error(err?.response?.data?.message || 'Could not unlock the period.');
    }
  }, [unlockTarget, refetch]);

  const columns: SyutGridColumn<PeriodLockDto>[] = useMemo(() => [
    {
      id: 'lockFrom', header: 'From', accessorKey: 'lockFrom', size: 120,
      cell: ({ row }) => fmtDate((row.original as PeriodLockDto).lockFrom),
    },
    {
      id: 'lockTo', header: 'To', accessorKey: 'lockTo', size: 120,
      cell: ({ row }) => fmtDate((row.original as PeriodLockDto).lockTo),
    },
    {
      id: 'lockSource', header: 'Source', accessorKey: 'lockSource', size: 140,
      cell: ({ row }) => SOURCE_LABEL[(row.original as PeriodLockDto).lockSource]
        || (row.original as PeriodLockDto).lockSource,
    },
    {
      id: 'lockReason', header: 'Reason', accessorKey: 'lockReason', size: 240,
      cell: ({ row }) => (row.original as PeriodLockDto).lockReason || '—',
    },
    {
      id: 'status', header: 'Status', size: 120,
      cell: () => {
        return (
          <span className="badge" style={{ background: '#6b7280', color: '#fff' }}>
            🔒 Locked
          </span>
        );
      },
    },
    {
      id: 'createdByName', header: 'Locked By', accessorKey: 'createdByName', size: 160,
      cell: ({ row }) => (row.original as PeriodLockDto).createdByName || '—',
    },
  ], []);

  const getRowActions = useCallback((rec: PeriodLockDto): ActionMenuItem[] => {
    if (!can('UPDATE') && !can('DELETE')) return [];
    return [{
      id: 'unlock', label: 'Unlock',
      icon: <IconSystem.refresh size={18} />,
      onClick: () => setUnlockTarget(rec),
      variant: 'danger',
    }];
  }, [can]);

  const actionButtons: ActionButton[] = [
    {
      ...actionButtonFactory.add(() => setFormOpen(true), 'Lock a period'),
      label: 'Lock Period',
      visible: can('CREATE'),
    },
    actionButtonFactory.refresh(refetch),
  ];

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({ ...prev, includeInactive: includeInactive || undefined, page: 1 }));
    setFilterOpen(false);
  }, [includeInactive]);

  const clearFilters = useCallback(() => {
    setIncludeInactive(false);
    setQueryParams(prev => ({ page: 1, pageSize: prev.pageSize }));
  }, []);

  return (
    <EnterpriseScreenShell
      title="Period Locks"
      description="Close / lock accounting periods — vouchers within a locked range become read-only"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.settings size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search reason / source...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount: includeInactive ? 1 : 0,
      }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)} /> Show unlocked history
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      {viewMode === 'list' && (
        <div className="data-grid-container">
          <SyutGrid data={data} columns={columns} enablePagination manualPagination
            totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange}
            rowActions={getRowActions}
            emptyMessage={loading ? 'Loading...' : 'No period locks — click Lock Period to close a range.'} />
        </div>
      )}
      {viewMode === 'grid' && (
        <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div style={{
            padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
          }}>
            {data.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                <IconSystem.settings size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p>{loading ? 'Loading...' : 'No period locks — click Lock Period to close a range.'}</p>
              </div>
            ) : (
              data.map(l => {
                const actions = getRowActions(l);
                return (
                  <div key={l.id} style={{
                    background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                    borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                          {fmtDate(l.lockFrom)} → {fmtDate(l.lockTo)}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                          {SOURCE_LABEL[l.lockSource] || l.lockSource}
                        </div>
                      </div>
                      {actions.length > 0 && <ActionMenu items={actions} />}
                    </div>
                    {l.lockReason && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{l.lockReason}</div>
                    )}
                    <div style={{
                      display: 'flex', gap: '0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)',
                      paddingTop: '0.5rem', borderTop: '1px solid var(--border-color)', alignItems: 'center',
                    }}>
                      <span>{l.createdByName || '—'}</span>
                      <span className="badge" style={{
                        marginLeft: 'auto',
                        background: '#6b7280',
                        color: '#fff',
                      }}>
                        🔒 Locked
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <PeriodLockFormModal isOpen={formOpen} onSaved={handleSaved} onClose={() => setFormOpen(false)} />

      <ConfirmDialog open={!!unlockTarget} title="Unlock Period"
        message={unlockTarget
          ? `Unlock ${fmtDate(unlockTarget.lockFrom)} – ${fmtDate(unlockTarget.lockTo)}? Vouchers in this range become editable again.`
          : ''}
        confirmLabel="Unlock" cancelLabel="Cancel" variant="danger"
        loading={isUnlocking} loadingMessage="Unlocking..."
        onConfirm={handleConfirmUnlock} onCancel={() => setUnlockTarget(null)} />
    </EnterpriseScreenShell>
  );
};

export default PeriodLockScreen;
